import React, { useRef, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { CardScrollControls, SizeGuideCard } from 'components/molecules';
import { H2, Paragraph, MarkdownWrapper } from 'components/atoms';
import './CardStrip.scss';

const CardStrip = ({
	heading,
	description,
	cardData,
	content,
	removeMinHeight,
}) => {
	// Ensure cardData is always an array to prevent runtime errors
	const safeCardData = Array.isArray(cardData) ? cardData : [];

	const scrollRef = useRef(null);
	const thumbRef = useRef(null);
	const trackRef = useRef(null);
	const [isDesktop, setIsDesktop] = useState(false);

	useEffect(() => {
		const handleResize = () => {
			setIsDesktop(window.innerWidth >= 1025);
		};

		handleResize(); // initial check
		window.addEventListener('resize', handleResize);

		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const updateThumb = useCallback(() => {
		const scrollEl = scrollRef.current;
		const thumbEl = thumbRef.current;
		const trackEl = trackRef.current;
		if (!scrollEl || !thumbEl || !trackEl) return;
		const scrollWidth = scrollEl.scrollWidth || 1;
		const visibleWidth = scrollEl.clientWidth || 1;
		const scrollLeft = scrollEl.scrollLeft || 0;

		const thumbWidth = (visibleWidth / scrollWidth) * trackEl.clientWidth;
		const maxThumbLeft = trackEl.clientWidth - thumbWidth;
		const thumbLeft =
			(scrollLeft / (scrollWidth - visibleWidth)) * maxThumbLeft;

		thumbEl.style.width = `${thumbWidth}px`;
		thumbEl.style.left = `${thumbLeft}px`;
	}, []);

	useEffect(() => {
		const scrollEl = scrollRef.current;

		updateThumb();
		scrollEl?.addEventListener('scroll', updateThumb);

		return () => scrollEl?.removeEventListener('scroll', updateThumb);
	}, [updateThumb]);

	const handleScroll = (direction) => {
		const scrollEl = scrollRef.current;

		const cardEl = scrollEl.querySelector('.size-guide-card');
		if (!cardEl) return;

		const cardWidth = cardEl.offsetWidth + 16;

		scrollEl.scrollBy({
			left: direction === 'left' ? -cardWidth : cardWidth,
			behavior: 'smooth',
		});
	};

	const handleLeftClick = () => handleScroll('left');
	const handleRightClick = () => handleScroll('right');

	const handleLeftKeyDown = (e) => {
		if (e.key === 'Enter' || e.key === ' ') handleScroll('left');
	};

	const handleRightKeyDown = (e) => {
		if (e.key === 'Enter' || e.key === ' ') handleScroll('right');
	};

	const showScrollControls = !isDesktop || safeCardData.length > 3;

	return (
		<div className="card-row-section">
			{/* Header Section */}
			<div className="card-row-header">
				<div className="header-content">
					{heading && (
						<H2
							weight="extraBold"
							size="large"
							className="card-row-heading"
						>
							{heading}
						</H2>
					)}
					{description && (
						<Paragraph
							weight="standard"
							size="small"
							className="card-row-title"
						>
							<MarkdownWrapper
								content={[{ value: description }]}
							/>
						</Paragraph>
					)}
				</div>
			</div>

			{/* Card Row Container */}
			<div
				className="card-row-container"
				data-testid="card-row-container"
			>
				<div
					className="card-row-wrapper"
					ref={scrollRef}
				>
					<div
						className={`card-row ${safeCardData.length === 2 ? 'two-cards' : ''}`}
					>
						{safeCardData.map((card, idx) => (
							<SizeGuideCard
								key={idx}
								{...card}
								removeMinHeight={removeMinHeight}
							/>
						))}
					</div>
				</div>

				{showScrollControls && (
					<CardScrollControls
						onLeftClick={handleLeftClick}
						onRightClick={handleRightClick}
						onLeftKeyDown={handleLeftKeyDown}
						onRightKeyDown={handleRightKeyDown}
						trackRef={trackRef}
						thumbRef={thumbRef}
					/>
				)}
			</div>

			{/* Description below */}
			{content && (
				<MarkdownWrapper
					className="size-guide-description"
					weight="standard"
					size="small"
					content={[{ value: content }]}
				/>
			)}
		</div>
	);
};

CardStrip.propTypes = {
	heading: PropTypes.string,
	description: PropTypes.string,
	cardData: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string.isRequired,
			description: PropTypes.string,
			content: PropTypes.string.isRequired,
			size: PropTypes.string,
			imageSrc: PropTypes.string,
		}),
	),
	content: PropTypes.string,
	removeMinHeight: PropTypes.bool,
};

CardStrip.defaultProps = {
	heading: '',
	description: '',
	cardData: [],
	content: '',
	removeMinHeight: false,
};

export default CardStrip;
